import { DownOutlined, EditOutlined, LogoutOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { initials } from '../lib/avatar';

const GAP = 6;
const PANEL_WIDTH = 220;
const MOBILE_PANEL_WIDTH = 220;

function panelWidth(triggerRect: DOMRect) {
  const edge = 8;
  const max = window.innerWidth - edge * 2;
  if (window.innerWidth <= 760) {
    // Compact dropdown from the avatar — do not stretch for long emails.
    return Math.min(MOBILE_PANEL_WIDTH, max);
  }
  return Math.min(Math.max(triggerRect.width, PANEL_WIDTH), max);
}

/**
 * Account trigger in the topbar: avatar + display name + chevron, opening a
 * menu with the email, Edit Profile, and sign-out. Closes on outside click,
 * Escape, or after the action runs.
 *
 * The panel is portaled to `document.body` and positioned with `fixed`
 * coordinates measured from the trigger, so it is never clipped by overflow
 * on the shell chrome.
 */
export function NavUserMenu({
  email,
  displayName,
  username,
  avatarUrl,
  mobilePanelExtra,
  personalLabel,
  editProfileLabel,
  signOutLabel,
  onSignOut,
}: {
  email: string;
  displayName?: string;
  username?: string;
  avatarUrl?: string | null;
  /** Shown inside the panel on mobile only — e.g. inline org switcher. */
  mobilePanelExtra?: ReactNode;
  personalLabel: string;
  editProfileLabel: string;
  signOutLabel: string;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<{ left: number; width: number; top?: number; bottom?: number }>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const label = displayName?.trim() || username?.trim() || personalLabel;
  const avatarSeed = displayName?.trim() || username?.trim() || email;

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = panelWidth(rect);
    // Prefer right-aligning under a topbar trigger so the panel stays on-screen
    // near the right edge; fall back to left-align when there isn't room.
    const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
    if (rect.top > window.innerHeight / 2) {
      setStyle({ left, width, bottom: window.innerHeight - rect.top + GAP });
    } else {
      setStyle({ left, width, top: rect.bottom + GAP });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Nested SelectMenu portals its list to document.body — treat that panel
    // as still "inside" this menu so opening/picking an org does not dismiss us.
    const inNestedSelect = (target: EventTarget | null) =>
      target instanceof Element && Boolean(target.closest('.select-menu-panel'));
    const isOutside = (target: EventTarget | null) =>
      !triggerRef.current?.contains(target as Node) &&
      !panelRef.current?.contains(target as Node) &&
      !inNestedSelect(target);
    const onPointerDown = (e: PointerEvent) => {
      if (isOutside(e.target)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReflow = () => setOpen(false);
    const onScroll = (e: Event) => {
      if (inNestedSelect(e.target)) return;
      onReflow();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  return (
    <div className="nav-user-menu">
      <button
        ref={triggerRef}
        type="button"
        className="nav-user-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        title={label !== email ? `${label} · ${email}` : email}
        onClick={() => setOpen((v) => !v)}
      >
        {avatarUrl ? (
          <img className="nav-user-avatar nav-user-avatar--img" src={avatarUrl} alt="" />
        ) : (
          <span className="nav-user-avatar" aria-hidden="true">
            {initials(avatarSeed)}
          </span>
        )}
        <span className="nav-user-name">{label}</span>
        <DownOutlined className={`nav-user-chevron${open ? ' is-open' : ''}`} aria-hidden="true" />
      </button>
      {open &&
        style &&
        createPortal(
          <div
            ref={panelRef}
            className="nav-user-panel"
            role="menu"
            id={menuId}
            style={{ position: 'fixed', left: style.left, width: style.width, top: style.top, bottom: style.bottom }}
          >
            <div className="nav-user-panel-header" role="presentation" title={email}>
              {email}
            </div>
            {mobilePanelExtra && (
              <>
                <div className="nav-user-panel-divider" role="separator" />
                <div className="nav-user-panel-mobile-only">{mobilePanelExtra}</div>
              </>
            )}
            <div className="nav-user-panel-divider" role="separator" />
            <Link
              to="/profile/edit"
              role="menuitem"
              className="nav-user-panel-item"
              onClick={() => setOpen(false)}
            >
              <EditOutlined aria-hidden="true" />
              {editProfileLabel}
            </Link>
            <button
              type="button"
              role="menuitem"
              className="nav-user-panel-item nav-user-panel-item--danger"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
            >
              <LogoutOutlined aria-hidden="true" />
              {signOutLabel}
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}
